// src/pages/vendor/Profile.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Badge,
} from "@mui/material";
import {
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Store,
  Email,
  Phone,
  LocationOn,
  Star,
  ShoppingBag,
  Verified,
  AccountBalance,
  Warning,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { vendorApi } from "../../api/vendorApi";
import { getUserAvatarUrl } from "../../utils/imageUtils";

const VendorProfile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [profileData, setProfileData] = useState({
    storeName: "",
    storeDescription: "",
    businessEmail: "",
    businessPhone: "",
    businessAddress: "",
    bankAccountName: "",
    bankAccountNumber: "",
    bankName: "",
  });

  const userAvatarUrl = getUserAvatarUrl(user);
  const displayAvatarUrl = previewImage || userAvatarUrl;

  useEffect(() => {
    if (user) {
      setProfileData({
        storeName: user.storeName || "",
        storeDescription: user.storeDescription || "",
        businessEmail: user.businessEmail || user.email || "",
        businessPhone: user.businessPhone || "",
        businessAddress: user.businessAddress || "",
        bankAccountName: user.bankAccountName || "",
        bankAccountNumber: user.bankAccountNumber || "",
        bankName: user.bankName || "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('storeLogo', selectedFile);
      
      const response = await vendorApi.updateStoreLogo(formData);
      updateUser(response.data.vendor);
      setOpenImageDialog(false);
      setSelectedFile(null);
      setPreviewImage(null);
      setSuccess('Store logo updated successfully!');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await vendorApi.updateProfile(profileData);
      updateUser(response.data.vendor);
      setEditing(false);
      setSuccess("Profile updated successfully!");
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setProfileData({
      storeName: user.storeName || "",
      storeDescription: user.storeDescription || "",
      businessEmail: user.businessEmail || user.email || "",
      businessPhone: user.businessPhone || "",
      businessAddress: user.businessAddress || "",
      bankAccountName: user.bankAccountName || "",
      bankAccountNumber: user.bankAccountNumber || "",
      bankName: user.bankName || "",
    });
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const getAvatarLetter = () => {
    if (user?.storeName) return user.storeName[0].toUpperCase();
    return 'S';
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              fontWeight="bold"
              sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
            >
              Store Profile
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}
            >
              Manage your store information and settings
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            {editing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  disabled={loading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={loading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => setEditing(true)}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
              >
                Edit Profile
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ 
        p: { xs: 2, sm: 3 }, 
        mb: 3, 
        borderRadius: 3, 
        textAlign: "center",
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
      }}>
        <Stack alignItems="center" spacing={2}>
          <Box sx={{ position: "relative" }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                  onClick={() => setOpenImageDialog(true)}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <PhotoCamera sx={{ fontSize: { xs: 16, sm: 20 } }} />
                </IconButton>
              }
            >
              <Avatar
                sx={{
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                  bgcolor: displayAvatarUrl ? 'transparent' : 'primary.main',
                  fontSize: { xs: 36, sm: 48 },
                  border: '3px solid',
                  borderColor: 'background.paper',
                  boxShadow: theme.shadows[3],
                }}
                src={displayAvatarUrl}
              >
                {!displayAvatarUrl && getAvatarLetter()}
              </Avatar>
            </Badge>
          </Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight="bold"
            sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
          >
            {user.storeName || "Store Name"}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <Chip
              icon={<Star sx={{ color: "#ffc107" }} />}
              label={`${user.rating || 0} Rating`}
              sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
            />
            <Chip
              icon={<ShoppingBag />}
              label={`${user.totalSales || 0} Sales`}
              sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
            />
            {user.isVerified && (
              <Chip icon={<Verified />} label="Verified" color="success" />
            )}
            {!user.isVerified && (
              <Chip 
                icon={<Warning />} 
                label="Pending Verification" 
                color="warning" 
                variant="outlined"
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={isMobile ? 2 : 3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
          }}>
            <CardContent>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                sx={{ mb: 2, color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
              >
                <Store sx={{ mr: 1, verticalAlign: 'middle' }} />
                Store Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Store Name
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="storeName"
                      value={profileData.storeName}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                      InputProps={{
                        sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
                      }}
                    />
                  ) : (
                    <Typography 
                      variant="body1" 
                      fontWeight="500"
                      sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
                    >
                      {user.storeName || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Description
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="storeDescription"
                      value={profileData.storeDescription}
                      onChange={handleChange}
                      multiline
                      rows={3}
                      size="small"
                      sx={{ mt: 0.5 }}
                      InputProps={{
                        sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
                      }}
                    />
                  ) : (
                    <Typography 
                      variant="body2"
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                      }}
                    >
                      {user.storeDescription || "No description provided"}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
          }}>
            <CardContent>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                sx={{ mb: 2, color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
              >
                <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
                Contact Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Business Email
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="businessEmail"
                      value={profileData.businessEmail}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                      InputProps={{
                        sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
                      }}
                    />
                  ) : (
                    <Typography 
                      variant="body1"
                      sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
                    >
                      {user.businessEmail || user.email || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Phone
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="businessPhone"
                      value={profileData.businessPhone}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                      InputProps={{
                        sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
                      }}
                    />
                  ) : (
                    <Typography 
                      variant="body1"
                      sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
                    >
                      {user.businessPhone || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    Address
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="businessAddress"
                      value={profileData.businessAddress}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                      InputProps={{
                        sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
                      }}
                    />
                  ) : (
                    <Typography 
                      variant="body1"
                      sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
                    >
                      {user.businessAddress || "Not set"}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
          }}>
            <CardContent>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                sx={{ mb: 2, color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
              >
                <AccountBalance sx={{ mr: 1, verticalAlign: "middle" }} />
                Payout Bank Details
              </Typography>
              <Typography
                variant="caption"
                sx={{ 
                  display: "block", 
                  mb: 2,
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                }}
              >
                These details are used for Admin Payouts and Vendor Withdrawals.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                        fontWeight: 600,
                      }}
                    >
                      Bank Account Name
                    </Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        name="bankAccountName"
                        value={profileData.bankAccountName}
                        onChange={handleChange}
                        size="small"
                        sx={{ mt: 0.5 }}
                        InputProps={{
                          sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
                        }}
                      />
                    ) : (
                      <Typography 
                        variant="body1"
                        sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
                      >
                        {user.bankAccountName || "Not set"}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                        fontWeight: 600,
                      }}
                    >
                      Bank Account Number
                    </Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        name="bankAccountNumber"
                        value={profileData.bankAccountNumber}
                        onChange={handleChange}
                        size="small"
                        sx={{ mt: 0.5 }}
                        InputProps={{
                          sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
                        }}
                      />
                    ) : (
                      <Typography 
                        variant="body1"
                        sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
                      >
                        {user.bankAccountNumber || "Not set"}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                        fontWeight: 600,
                      }}
                    >
                      Bank Name
                    </Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        name="bankName"
                        value={profileData.bankName}
                        onChange={handleChange}
                        size="small"
                        sx={{ mt: 0.5 }}
                        InputProps={{
                          sx: { color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }
                        }}
                      />
                    ) : (
                      <Typography 
                        variant="body1"
                        sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
                      >
                        {user.bankName || "Not set"}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ 
            p: { xs: 2, sm: 3 }, 
            borderRadius: 3,
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.paper',
          }}>
            <Typography 
              variant="h6" 
              fontWeight="bold" 
              sx={{ mb: 2, color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}
            >
              Store Statistics
            </Typography>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    fontWeight="bold" 
                    color="primary"
                  >
                    {user.totalProducts || 0}
                  </Typography>
                  <Typography 
                    variant="caption"
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                    }}
                  >
                    Products
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    fontWeight="bold" 
                    color="primary"
                  >
                    {user.totalOrders || 0}
                  </Typography>
                  <Typography 
                    variant="caption"
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                    }}
                  >
                    Orders
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    fontWeight="bold" 
                    color="primary"
                  >
                    {user.totalCustomers || 0}
                  </Typography>
                  <Typography 
                    variant="caption"
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                    }}
                  >
                    Customers
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    fontWeight="bold" 
                    color="primary"
                  >
                    {user.commissionRate || 0}%
                  </Typography>
                  <Typography 
                    variant="caption"
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                    }}
                  >
                    Commission Rate
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit' }}>
          Update Store Logo
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Avatar
              sx={{
                width: { xs: 100, sm: 120 },
                height: { xs: 100, sm: 120 },
                mx: 'auto',
                mb: 2,
                bgcolor: 'primary.main',
              }}
              src={previewImage || userAvatarUrl}
            >
              {!previewImage && !userAvatarUrl && getAvatarLetter()}
            </Avatar>
            <Button 
              variant="outlined" 
              component="label"
              sx={{ 
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'inherit',
              }}
            >
              Choose New Logo
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileSelect}
              />
            </Button>
            {selectedFile && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 1,
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                }}
              >
                {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenImageDialog(false)} fullWidth={isMobile}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadLogo}
            disabled={!selectedFile || uploading}
            fullWidth={isMobile}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorProfile;